import { calendarDaysBetween } from '../../shared/dates';
import { effectiveInvoiceStatus } from '../../shared/finance';
import type { ExistingInvoice, InvoiceDraftResponse } from '../../shared/invoicing';

export interface ProjectMoneyRow {
  id: string | null;
  number: string | null;
  label: 'Deposit' | 'Remainder' | 'Full' | 'Other' | 'Invoice';
  amount_cents: number;
  gst_cents: number;
  state: 'not_drafted' | 'draft' | 'sent' | 'paid' | 'overdue' | 'void';
  date: string | null;
  days_overdue: number;
}

const labels = { deposit: 'Deposit', remainder: 'Remainder', full: 'Full', other: 'Other' } as const;

/** Keep every actual invoice, including repeated kinds and void history. The
 * strip carries authoritative pending amounts and aggregate totals; real rows
 * carry their own identity, explicit kind, cents and dates. Never infer a kind
 * from a number or from a quoted percentage that is not in the contract. */
export function projectMoneyRows(draft: InvoiceDraftResponse | null | undefined, fallback: ExistingInvoice[], today: string): ProjectMoneyRow[] {
  if (draft?.strip.free) return [];
  const invoices = draft?.invoiced.existing ?? fallback;
  const depositId = draft?.invoiced.deposit?.invoice_id;
  const rows: ProjectMoneyRow[] = invoices.map((invoice) => {
    const state = effectiveInvoiceStatus(invoice.status, invoice.due_date, today);
    return {
      id: invoice.id, number: invoice.invoice_number,
      label: invoice.id === depositId ? 'Deposit' : invoice.invoice_kind ? labels[invoice.invoice_kind] : 'Invoice',
      amount_cents: invoice.amount_cents, gst_cents: invoice.gst_cents,
      state, date: state === 'paid' ? invoice.paid_date : invoice.due_date,
      days_overdue: state === 'overdue' && invoice.due_date ? Math.max(0, calendarDaysBetween(invoice.due_date, today)) : 0,
    };
  });
  if (draft?.strip.mode === 'labelled') {
    for (const segment of draft.strip.segments) {
      if (segment.state !== 'none' && segment.state !== 'not_drafted') continue;
      // The server's Quote placeholder is the full remaining amount. With no
      // deposit configuration there is no evidence for a 50% / 50% split.
      const label = segment.label === 'Quote' ? (draft.draft?.kind === 'remainder' ? 'Remainder' : 'Full') : segment.label;
      rows.push({ id: null, number: null, label, amount_cents: segment.amount_cents,
        gst_cents: segment.gst_cents, state: 'not_drafted', date: null, days_overdue: 0 });
    }
  }
  const order = ['Deposit', 'Remainder', 'Full', 'Other', 'Invoice'];
  return rows.sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label));
}
